import { Stack, useRouter } from 'expo-router';
import { ScrollView, YStack } from 'tamagui';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import PSCircleButton from '@/components/ui/PSCircleButton';
import PSIcon from '@/components/ui/PSIcon';
import PSSkeleton from '@/components/ui/PSSkeleton';
import { space } from '@/constants/tokens';
import PageScrollView from './ui/PageScrollView';

/**
 * EventDetailSkeleton — the loading placeholder for `app/event/[id]`.
 *
 * Shown while `useFetchEvent` is in flight (see the screen's `loading` branch).
 * It mirrors the shape of `EventDetail` closely enough that the real page slots
 * in without a jump: the same transparent-over-hero native header (back button
 * only — nothing to share yet), the same scroll + gutter, and a hero bone at the
 * exact 3:4 ratio the `ImageCarousel` uses, so the biggest element lands in the
 * same place before and after load.
 *
 * The hero's height comes from its width the same way the real carousel's does —
 * but here via `aspectRatio` (width:height = 3:4) rather than an `onLayout`
 * measure, since a bone has no image to size to: the flexed full-width bone gets
 * its height from the ratio for free. Below it, a handful of text-line bones
 * stand in for the title / meta / about block so the screen reads as "an event,
 * loading" rather than a blank pulse.
 *
 * Header: only the back button (copied from `EventDetail` — the same
 * `headerLeft` + iOS-26 glass-suppressed `unstable_headerLeftItems` pair). There
 * is deliberately no share control: there's nothing to share until the event
 * loads.
 */
export default function EventDetailSkeleton() {
  const router = useRouter();
  const { top: safeAreaTop } = useSafeAreaInsets();

  // Same filled back circle as EventDetail's header (defined once, wired in two
  // ways below so iOS 26 gets the glass-suppressed path). See EventDetail for the
  // full explanation of the two header paths.
  const backButton = (
    <PSCircleButton
      filled
      onPress={() => (router.canGoBack() ? router.back() : router.push('/'))}
    >
      <PSIcon name="arrow-back" />
    </PSCircleButton>
  );

  return (
    <YStack flex={1} bg="$color.background">
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,
          headerLeft: () => backButton,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: backButton,
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      <PageScrollView>
        <PSSkeleton>
          <YStack gap="$space.xl">
            {/* Hero — full width, 3:4 (width:height), matching ImageCarousel's
                ratio so the real image lands in the same box on load. */}
            <PSSkeleton.Bone style={{ width: '100%', aspectRatio: 3 / 4 }} />

            {/* Title + meta lines (stand in for EventDetailMeta). */}
            <YStack gap="$sm">
              <PSSkeleton.Bone style={{ height: 28, width: '80%' }} />
              <PSSkeleton.Bone style={{ height: 16, width: '55%' }} />
              <PSSkeleton.Bone style={{ height: 16, width: '40%' }} />
            </YStack>

            {/* About block — a heading line + a few body lines. */}
            <YStack gap="$sm">
              <PSSkeleton.Bone style={{ height: 20, width: '30%' }} />
              <PSSkeleton.Bone style={{ height: 14, width: '100%' }} />
              <PSSkeleton.Bone style={{ height: 14, width: '100%' }} />
              <PSSkeleton.Bone style={{ height: 14, width: '70%' }} />
            </YStack>
          </YStack>
        </PSSkeleton>
      </PageScrollView>
    </YStack>
  );
}
